import React from "react";
import Icon from "components/AppIcon";
import Input from "components/ui/Input";

const JURISDICTIONS = [
  { value: "england-wales", label: "England & Wales" },
  { value: "scotland", label: "Scotland" },
  { value: "cayman-islands", label: "Cayman Islands" },
  { value: "luxembourg", label: "Luxembourg" },
  { value: "ireland", label: "Ireland" },
  { value: "jersey", label: "Jersey" },
  { value: "guernsey", label: "Guernsey" },
  { value: "delaware", label: "Delaware, USA" },
  { value: "singapore", label: "Singapore" },
];

const VEHICLE_TYPES = [
  {
    value: "limited-partnership",
    label: "Limited Partnership",
    description: "Traditional LP structure with GP/LP separation. Preferred for UK infrastructure funds.",
    icon: "Users",
  },
  {
    value: "llp",
    label: "Limited Liability Partnership",
    description: "Flexible partnership with limited liability for all partners. Common in UK professional structures.",
    icon: "Shield",
  },
  {
    value: "private-limited",
    label: "Private Limited Company",
    description: "Incorporated entity with share capital. Suitable for single-asset SPVs.",
    icon: "Building2",
  },
  {
    value: "unit-trust",
    label: "Unit Trust",
    description: "Collective investment scheme structure. Regulated by FCA under COLL sourcebook.",
    icon: "Layers",
  },
];

const SECTORS = [
  { value: "energy", label: "Energy & Renewables" },
  { value: "transport", label: "Transport & Logistics" },
  { value: "digital", label: "Digital Infrastructure" },
  { value: "water", label: "Water & Utilities" },
  { value: "social", label: "Social Infrastructure" },
  { value: "healthcare", label: "Healthcare Facilities" },
];

export default function StepSPVBasics({ data, onChange, errors }) {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      <div>
        <h3 className="font-heading text-lg lg:text-xl font-semibold text-foreground mb-1">
          SPV Basics
        </h3>
        <p className="font-caption text-sm text-muted-foreground">
          Define the fundamental structure and identity of your Special Purpose Vehicle.
        </p>
      </div>
      {/* SPV Name */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div>
          <Input
            label="SPV Name"
            type="text"
            placeholder="e.g. SeedDrive Renewables SPV I LP"
            value={data?.name || ""}
            onChange={(e) => handleChange("name", e?.target?.value)}
            required
            error={errors?.name}
            description="Use a descriptive name that identifies the asset and vintage year."
          />
        </div>
        <div>
          <Input
            label="Reference Code"
            type="text"
            placeholder="e.g. SDR-2026-001"
            value={data?.referenceCode || ""}
            onChange={(e) => handleChange("referenceCode", e?.target?.value)}
            description="Internal reference code for tracking and reporting purposes."
          />
        </div>
      </div>
      {/* Jurisdiction & Sector */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div>
          <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
            Jurisdiction <span className="text-error">*</span>
          </label>
          <select
            value={data?.jurisdiction || ""}
            onChange={(e) => handleChange("jurisdiction", e?.target?.value)}
            className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            style={{ borderColor: errors?.jurisdiction ? "var(--color-error)" : "var(--color-border)" }}
          >
            <option value="">Select jurisdiction…</option>
            {JURISDICTIONS?.map((j) => (
              <option key={j?.value} value={j?.value}>{j?.label}</option>
            ))}
          </select>
          {errors?.jurisdiction && (
            <p className="mt-1 text-xs font-caption text-error">{errors?.jurisdiction}</p>
          )}
          <p className="mt-1 text-xs font-caption text-muted-foreground">
            Legal domicile for the SPV entity registration.
          </p>
        </div>
        <div>
          <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
            Primary Sector <span className="text-error">*</span>
          </label>
          <select
            value={data?.sector || ""}
            onChange={(e) => handleChange("sector", e?.target?.value)}
            className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            style={{ borderColor: errors?.sector ? "var(--color-error)" : "var(--color-border)" }}
          >
            <option value="">Select sector…</option>
            {SECTORS?.map((s) => (
              <option key={s?.value} value={s?.value}>{s?.label}</option>
            ))}
          </select>
          {errors?.sector && (
            <p className="mt-1 text-xs font-caption text-error">{errors?.sector}</p>
          )}
        </div>
      </div>
      {/* Vehicle Type */}
      <div>
        <label className="block font-caption text-sm font-medium text-foreground mb-3">
          Vehicle Type <span className="text-error">*</span>
        </label>
        {errors?.vehicleType && (
          <p className="mb-2 text-xs font-caption text-error">{errors?.vehicleType}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {VEHICLE_TYPES?.map((vt) => {
            const isSelected = data?.vehicleType === vt?.value;
            return (
              <button
                key={vt?.value}
                type="button"
                onClick={() => handleChange("vehicleType", vt?.value)}
                className="flex items-start gap-3 p-4 rounded-lg border text-left transition-all duration-200 hover:shadow-sm"
                style={{
                  borderColor: isSelected ? "var(--color-primary)" : "var(--color-border)",
                  background: isSelected ? "rgba(59,76,76,0.05)" : "var(--color-card)",
                  boxShadow: isSelected ? "0 0 0 2px rgba(59,76,76,0.15)" : undefined,
                }}
              >
                <div
                  className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-md mt-0.5"
                  style={{
                    background: isSelected ? "var(--color-primary)" : "var(--color-muted)",
                  }}
                >
                  <Icon
                    name={vt?.icon}
                    size={18}
                    color={isSelected ? "white" : "var(--color-muted-foreground)"}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-caption font-semibold text-sm text-foreground">
                      {vt?.label}
                    </span>
                    {isSelected && (
                      <Icon name="CheckCircle2" size={14} color="var(--color-primary)" />
                    )}
                  </div>
                  <p className="font-caption text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {vt?.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Target Size & Currency */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <div>
          <Input
            label="Target Fund Size (£)"
            type="number"
            placeholder="e.g. 50000000"
            value={data?.targetSize || ""}
            onChange={(e) => handleChange("targetSize", e?.target?.value)}
            required
            error={errors?.targetSize}
            description="Total equity capital to be raised."
          />
        </div>
        <div>
          <Input
            label="Minimum Ticket Size (£)"
            type="number"
            placeholder="e.g. 500000"
            value={data?.minTicket || ""}
            onChange={(e) => handleChange("minTicket", e?.target?.value)}
            description="Minimum individual investor commitment."
          />
        </div>
        <div>
          <Input
            label="Target IRR (%)"
            type="number"
            placeholder="e.g. 12.5"
            value={data?.targetIRR || ""}
            onChange={(e) => handleChange("targetIRR", e?.target?.value)}
            description="Gross unlevered target internal rate of return."
          />
        </div>
      </div>
      {/* Description */}
      <div>
        <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
          SPV Description
        </label>
        <textarea
          value={data?.description || ""}
          onChange={(e) => handleChange("description", e?.target?.value)}
          placeholder="Briefly describe the investment thesis, target asset, and key value drivers…"
          rows={4}
          className="w-full px-3 py-2.5 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          style={{ borderColor: "var(--color-border)" }}
        />
        <p className="mt-1 text-xs font-caption text-muted-foreground">
          This description will appear in investor communications and the data room.
        </p>
      </div>
    </div>
  );
}